import useDeleteCustomTemplateMutation from 'components/domain/automation/customTemplates/useDeleteCustomTemplateMutation';
import { modalIds } from 'components/modal/Modal.constants';
import useAppModal from 'hooks/useAppModal';
import { CustomTemplate } from 'models/automation/CustomTemplates';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useNotify from 'hooks/useNotify';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { CUSTOM_TEMPLATES_BASE_QUERY_KEY } from 'components/domain/automation/customTemplates/customTemplatesQuery';

const useDeleteCustomTemplateModal = ({
  closeAdditionalModals = [],
}: {
  closeAdditionalModals?: string[];
} = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotify();
  const { openConfirmModal, closeModal } = useAppModal();
  const { mutateAsync: deleteCustomTemplate } = useDeleteCustomTemplateMutation(
    {
      onSuccess: () => {
        notifySuccess(t('pageAutomations.customTemplates.deleteModal.success'));
        closeModal(modalIds.confirm);

        if (closeAdditionalModals.length > 0) {
          closeAdditionalModals.forEach((modal) => {
            closeModal(modal);
          });
        }

        queryClient.invalidateQueries({
          queryKey: [CUSTOM_TEMPLATES_BASE_QUERY_KEY],
        });
      },

      onError: (error) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage:
              error.response?.data?.apiErrorMessage ??
              t('pageAutomations.customTemplates.deleteModal.error'),
            baseKey: 'pageAutomations.customTemplates.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  const openDeleteCustomTemplateModal = (customTemplate: CustomTemplate) => {
    if (customTemplate.assignedTriggers?.length > 0) {
      openConfirmModal({
        title: t(
          'pageAutomations.customTemplates.deleteModal.assignedTriggers.title',
        ),
        body: (
          <div>
            <p>
              {t(
                'pageAutomations.customTemplates.deleteModal.assignedTriggers.descriptionName',
                { name: customTemplate.name },
              )}
            </p>
            <ul>
              {customTemplate.assignedTriggers?.map((trigger) => (
                <li key={trigger.uid}>{trigger.name}</li>
              ))}
            </ul>
            <div>
              {t(
                'pageAutomations.customTemplates.deleteModal.assignedTriggers.pleaseRemove',
              )}
            </div>
          </div>
        ),
        confirmColor: 'primary',
        confirmLabel: t(
          'pageAutomations.customTemplates.deleteModal.confirm.confirmButton',
        ),
        onConfirm: () => {
          closeModal(modalIds.confirm);
        },
      });
    } else {
      openConfirmModal({
        title: t('pageAutomations.customTemplates.deleteModal.confirm.title'),
        body: (
          <Trans
            i18nKey="pageAutomations.customTemplates.deleteModal.confirm.description"
            values={{ name: customTemplate.name }}
            components={{ br: <br /> }}
          />
        ),
        confirmColor: 'danger',
        confirmLabel: t('common.delete'),
        onConfirmAsync: () =>
          deleteCustomTemplate({ customTemplateUid: customTemplate.uid }),
      });
    }
  };

  return {
    openDeleteCustomTemplateModal,
  };
};

export default useDeleteCustomTemplateModal;
